import React, { useMemo } from "react";

// Import all field types
import TextField from "./fields/TextField";
import NumberField from "./fields/NumberField";
import SignatureField from "./fields/SignatureField";
import YesNoField from "./fields/YesNoField";
import CheckboxField from "./fields/CheckboxField";
import MultipleChoiceField from "./fields/MultipleChoiceField";
import SliderField from "./fields/SliderField";
import DateTimeField from "./fields/DateTimeField";
import LocationField from "./fields/LocationField";
import InstructionField from "./fields/InstructionField";
import DocumentNumberField from "./fields/DocumentNumberField";
import SectionRenderer from "./fields/SectionRenderer";
import MediaField from "./fields/MediaField";
import AssetField from "./fields/AssetField";
import { SPLIT_WORKFLOW_TYPE } from "../utils/workflowUtils";

/**
 * FieldRenderer:
 * - Routes a field to the correct component
 * - Handles branching logic
 * - Supports nested sections
 */
export default function FieldRenderer({
  formId,
  field,
  value,
  setValue,
  allFields,
  branchDepth = 0,
  openViewer,
  previewMode = false,
  companyId,
  // new props for preview rendering / nested children
  values = {},
  setValueFor = null,
  // when true, the parent renderer is already rendering branch children
  // and FieldRenderer should not render them itself (prevents duplicates)
  inlineChildrenHandledExternally = false,
}) {
  // -----------------------------------------
  // BRANCHING ENGINE → find triggered children
  // -----------------------------------------
  // compute branch children using the branch group tests similar to the builder
  const branchChildren = useMemo(() => {
    if (!field) return [];
    const groups = getBranchGroupsLocal(field);
    const result = [];
    for (const g of groups) {
      const cfg = field.branchMap?.[g.key];
      if (!cfg?.targets?.length) continue;
      const currentVal = (values && values[field.id] !== undefined) ? values[field.id] : value;
      const passes = g.test(currentVal, field);
      if (passes) {
        cfg.targets.forEach((childId) => {
          const f = allFields.find((ff) => ff.id === childId);
          if (f) result.push(f);
        });
      }
    }

    return result;
  }, [field, value, allFields, values]);

  // -----------------------------------------
  // TYPE ROUTER → return correct component
  // -----------------------------------------
  const renderFieldByType = () => {
    const props = {
      formId,
      field,
      value,
      setValue,
      allFields,
      branchDepth,
      openViewer,
      previewMode,
      companyId,
    };

    const renderInlineChildren = () => (
      <div className="mt-4 space-y-3">
        {branchChildren.map((child) => (
          <FieldRenderer
            key={child.id}
            formId={formId}
            field={child}
            // pass the child's own value and setter when available
            value={(values && values[child.id] !== undefined) ? values[child.id] : undefined}
            setValue={setValueFor ? ((v) => setValueFor(child.id, v)) : undefined}
            allFields={allFields}
            branchDepth={branchDepth + 1}
            openViewer={openViewer}
            previewMode={previewMode}
            companyId={companyId}
            values={values}
            setValueFor={setValueFor}
          />
        ))}
      </div>
    );

    const withBranching = {
      ...props,
      // only provide renderInlineChildren when the parent hasn't already taken
      // responsibility for rendering inline branch children.
      renderInlineChildren: !inlineChildrenHandledExternally && branchChildren.length > 0 ? renderInlineChildren : undefined,
      inlineChildrenHandledExternally,
    };

    // Normalize slider-like fields that may be authored as number with slider style
    const sliderHints = new Set([
      field?.config?.style,
      field?.config?.control,
      field?.config?.widget,
      field?.config?.renderAs,
      field?.config?.input,
      field?.input,
      field?.widget,
      field?.ui,
      field?.control,
      field?.renderAs,
    ].filter(Boolean));
    const minRaw = field?.config?.min;
    const maxRaw = field?.config?.max;
    const minNum = minRaw !== undefined && minRaw !== null ? Number(minRaw) : undefined;
    const maxNum = maxRaw !== undefined && maxRaw !== null ? Number(maxRaw) : undefined;
    const hasMinMax = typeof minNum === "number" && !isNaN(minNum) && typeof maxNum === "number" && !isNaN(maxNum) && minNum < maxNum;
    const isSliderLike = (
      field.type === "slider" ||
      (["number", "text"].includes(field.type) && (
        sliderHints.has("slider") ||
        field?.config?.inputType === "range" ||
        (hasMinMax && field?.config?.useSlider !== false)
      ))
    );

    switch (isSliderLike ? "slider" : field.type) {
      case "text":
        return <TextField {...withBranching} />;
      case "number":
        return <NumberField {...withBranching} />;
      case "signature":
        return <SignatureField {...withBranching} />;
      case "yesno":
        return <YesNoField {...withBranching} />;
      case "checkbox":
        return <CheckboxField {...withBranching} />;
      case "multiple_choice":
        return <MultipleChoiceField {...withBranching} />;
      case "slider":
        return <SliderField {...withBranching} />;
      case "date_time":
        return <DateTimeField {...withBranching} />;
      case "location":
        return <LocationField {...withBranching} />;
      case "instruction":
        return <InstructionField {...withBranching} />;
      case "document_number":
        return <DocumentNumberField {...withBranching} />;
      case "media":
        return <MediaField {...withBranching} />;
      case "asset":
        return <AssetField {...withBranching} />;
      case "section":
      case SPLIT_WORKFLOW_TYPE:
        // When rendering a section, avoid re-rendering branch children that are
        // already defined as children of this section. We create a filtered
        // renderInlineChildren to pass into SectionRenderer so duplicate
        // branching targets (which live inside the section) are not rendered twice.
        const sectionChildren = Array.isArray(field.children) && field.children.length > 0
          ? field.children
          : Array.isArray(field.fields)
            ? field.fields
            : [];
        const sectionChildIds = new Set(sectionChildren.map((c) => c.id));
        const renderInlineChildrenForSection = () => (
          <div className="mt-4 space-y-3">
            {branchChildren
              .filter((child) => !sectionChildIds.has(child.id))
              .map((child) => (
                <FieldRenderer
                  key={child.id}
                  formId={formId}
                  field={child}
                  value={(values && values[child.id] !== undefined) ? values[child.id] : undefined}
                  setValue={setValueFor ? ((v) => setValueFor(child.id, v)) : undefined}
                  allFields={allFields}
                  branchDepth={branchDepth + 1}
                  openViewer={openViewer}
                  previewMode={previewMode}
                  companyId={companyId}
                  values={values}
                  setValueFor={setValueFor}
                />
              ))}
          </div>
        );

        return (
            <SectionRenderer
              {...withBranching}
              // pass a filtered inline children renderer so section-internal
              // children don't get duplicated by branching rendering
              renderInlineChildren={branchChildren.length > 0 ? renderInlineChildrenForSection : undefined}
              renderChild={(childField, childValue, setChildVal) => (
                <FieldRenderer
                  formId={formId}
                  field={childField}
                  value={childValue}
                  setValue={setChildVal}
                  allFields={allFields}
                  branchDepth={branchDepth + 1}
                  previewMode={previewMode}
                  openViewer={openViewer}
                  companyId={companyId}
                  values={values}
                  setValueFor={setValueFor}
                  inlineChildrenHandledExternally={inlineChildrenHandledExternally}
                />
              )}
            />
          );

      default:
        return <div className="p-2 bg-red-100 text-red-700 rounded">Unknown field type: {field.type}</div>;
    }
  };

  return renderFieldByType();
}

// Helper to evaluate branch conditions
// removed unused shouldTrigger helper to satisfy eslint

// Local copy of branch group definitions used by the builder's visibility logic.
function getBranchGroupsLocal(field) {
  switch (field.type) {
    case "yesno":
      return [
        { key: "yes", label: "When answer is Yes", test: (v) => String(v?.answer ?? v).toLowerCase() === "yes" },
        { key: "no", label: "When answer is No", test: (v) => String(v?.answer ?? v).toLowerCase() === "no" },
      ];
    case "checkbox":
      return [
        { key: "checked", label: "When checkbox is checked", test: (v) => (v === true) || (v?.checked === true) },
        { key: "unchecked", label: "When checkbox is not checked", test: (v) => (v === false) || (v?.checked === false) },
      ];
    case "text":
      return [
        { key: "not_blank", label: "When answer is not blank", test: (v) => !!(v?.answer ?? v) && String((v?.answer ?? v)).trim().length > 0 },
      ];
    case "number":
      return [
        { key: "not_blank", label: "When number is provided", test: (v) => {
          const actual = v?.number ?? v?.answer ?? v;
          return actual !== null && actual !== "" && !isNaN(actual);
        }},
      ];
    case "date_time":
      return [
        {
          key: "not_blank",
          label: "When date/time is provided",
          test: (v) => {
            if (!v) return false;
            if (typeof v === "string") return v.trim() !== "";
            if (typeof v === "object") return !!(v.date || v.time || v.toString());
            return false;
          },
        },
      ];
    case "signature":
      return [
        { key: "signed", label: "When signed / typed", test: (v) => !!(v?.answer ?? v) && (typeof (v?.answer ?? v) === "string" ? (v?.answer ?? v).trim().length > 0 : true) },
      ];
    case "slider":
      return [
        { key: "value_set", label: "When slider has any value", test: (v) => typeof (v?.answer ?? v) === "number" && !isNaN(v?.answer ?? v) },
      ];
    case "multiple_choice":
      return (field.config?.options || []).map((opt) => ({
        key: `opt:${opt.id}`,
        label: `When option "${opt.label}" is selected`,
        test: (v, f) => {
          const currentField = f || field;
          const currentOpt = (currentField?.config?.options || []).find((o) => o.id === opt.id);
          if (!currentOpt) return false;
          // The value stored is the option label (or array of labels for multi-select)
          const actual = v?.answer ?? v;
          if (Array.isArray(actual)) return actual.includes(opt.label);
          return String(actual) === String(opt.label);
        }
      }));
    case "asset":
    case "location":
    case SPLIT_WORKFLOW_TYPE:
      return [
        { key: "not_blank", label: "When a value is selected", test: (v) => !!(v?.answer ?? v) && String((v?.answer ?? v)).trim().length > 0 },
      ];
    default:
      return [];
  }
}
